import {useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {
    Desk,
    EmployeeRef,
    Neighbourhood,
    SeatingPlan,
    SeatingPlanDeskAssignment,
    SeatingPlanExistingNeighbourhoodRef,
    SeatingPlanForm,
    SeatingPlanMove,
    UNASSIGNED_NEIGHBOURHOOD_ID
} from "../../api/Model";
import {useSeatingPlanStore} from "../../api/SeatingPlanStore";
import {AppPages, usePageUrl} from "../../app-layout/AppPages";
import {SeatingPlans_Admin} from "../../util/access/Permission";
import {usePermission} from "../../util/access/UseAccess";
import {useMessage} from "../../util/actions/Message";
import {toDeskRef} from "../../api/Projections";
import {Opt} from "../../util/Optional";
import {filterNotNull} from "../../util/ArrayUtil";
import {apiErrorMessage} from "../../util/ApiErrorMessage";

function existingHoodRef(neighbourhoodId: Neighbourhood['id']): SeatingPlanExistingNeighbourhoodRef {
    return {type: 'existing', id: neighbourhoodId};
}

export function asDeskAssignment(desk: Desk): SeatingPlanDeskAssignment {
    return {
        desk: toDeskRef(desk),
        neighbourhood: desk.neighbourhoodId != null ? existingHoodRef(desk.neighbourhoodId) : null,
        employee: desk.assignedTo,
    };
}

export function asEmployeeMoves(
    employee: EmployeeRef,
    removeNeighbourhoods: Opt<Neighbourhood>[],
    addNeighbourhoods: Opt<Neighbourhood>[],
): SeatingPlanMove[] {
    return [
        ...filterNotNull(removeNeighbourhoods)
            .filter(hood => hood.id !== UNASSIGNED_NEIGHBOURHOOD_ID)
            .map<SeatingPlanMove>(hood => ({
                employee,
                neighbourhood: existingHoodRef(hood.id),
                operation: 'remove',
            })),
        ...filterNotNull(addNeighbourhoods)
            .filter(hood => hood.id !== UNASSIGNED_NEIGHBOURHOOD_ID)
            .map<SeatingPlanMove>(hood => ({
                employee,
                neighbourhood: existingHoodRef(hood.id),
                operation: 'add',
            }))
    ];
}


export function useUnassignDesks(): (plan: SeatingPlan, desks: Desk[]) => Promise<void> {
    const {setDeskAssignment} = useSeatingPlanStore();

    return useCallback(async (plan: SeatingPlan, desks: Desk[]) => {
        await Promise.all(
            desks.map(desk =>
                setDeskAssignment(plan, desk, asDeskAssignment({...desk, assignedTo: null}))
            )
        );
    }, [setDeskAssignment])
}

export interface MessageHandler {
    success(message: string): void;
    error(message: string, error: any): void;
}

export const exceptionalMessageHandler: MessageHandler = {
    success() {},

    error(message: string, error: any) {
        throw new Error(apiErrorMessage(error) ?? message);
    },
}

export function useCreateAndOpenSeatingPlan(messageHandler?: MessageHandler) {
    const pageUrl = usePageUrl();
    const navigate = useNavigate();
    const message = useMessage();
    const {createPlan} = useSeatingPlanStore();
    const {canFix} = usePermission({canFix: SeatingPlans_Admin});

    const actualMessageHandler: MessageHandler = messageHandler ?? message;

    const fixAction = useCallback(async (
        seatingPlan: SeatingPlanForm,
        applyFixes: (seatingPlan: SeatingPlan) => Promise<void>,
    ) => {
        try {
            const plan = await createPlan(seatingPlan);
            await applyFixes(plan);
            actualMessageHandler.success('Created a seating plan with the proposed fixes.');
            navigate(pageUrl(AppPages.seatingPlan, {planId: plan.id}, {tab: 'summary'}));
        } catch (error) {
            actualMessageHandler.error('Failed to create the seating plan. Please try again later.', error);
        }
    }, [createPlan, message, navigate, pageUrl, actualMessageHandler]);

    return canFix ? fixAction : undefined;
}
